package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Download
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.AccountantExportFormat
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.AccountantExportViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoTextField
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.accountant_download
import evoshift.composeapp.generated.resources.accountant_email_label
import evoshift.composeapp.generated.resources.accountant_export_intro
import evoshift.composeapp.generated.resources.accountant_export_title
import evoshift.composeapp.generated.resources.accountant_format_attendance_pdf
import evoshift.composeapp.generated.resources.accountant_format_attendance_pdf_text
import evoshift.composeapp.generated.resources.accountant_format_enova
import evoshift.composeapp.generated.resources.accountant_format_enova_text
import evoshift.composeapp.generated.resources.accountant_format_generic_csv
import evoshift.composeapp.generated.resources.accountant_format_generic_csv_text
import evoshift.composeapp.generated.resources.accountant_format_optima
import evoshift.composeapp.generated.resources.accountant_format_optima_text
import evoshift.composeapp.generated.resources.accountant_format_package
import evoshift.composeapp.generated.resources.accountant_format_package_text
import evoshift.composeapp.generated.resources.accountant_format_symfonia
import evoshift.composeapp.generated.resources.accountant_format_symfonia_text
import evoshift.composeapp.generated.resources.accountant_format_time_record_pdf
import evoshift.composeapp.generated.resources.accountant_format_time_record_pdf_text
import evoshift.composeapp.generated.resources.accountant_format_time_record_xlsx
import evoshift.composeapp.generated.resources.accountant_format_time_record_xlsx_text
import evoshift.composeapp.generated.resources.accountant_missing_payroll_ids
import evoshift.composeapp.generated.resources.accountant_open_file
import evoshift.composeapp.generated.resources.accountant_ready
import evoshift.composeapp.generated.resources.accountant_send
import evoshift.composeapp.generated.resources.accountant_sent
import evoshift.composeapp.generated.resources.accountant_sent_link_only
import evoshift.composeapp.generated.resources.close
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** The name and one-line description of each format (EN + PL in the string resources). */
fun AccountantExportFormat.labels(): Pair<StringResource, StringResource> = when (this) {
    AccountantExportFormat.TIME_RECORD_PDF -> Res.string.accountant_format_time_record_pdf to Res.string.accountant_format_time_record_pdf_text
    AccountantExportFormat.TIME_RECORD_XLSX -> Res.string.accountant_format_time_record_xlsx to Res.string.accountant_format_time_record_xlsx_text
    AccountantExportFormat.ATTENDANCE_PDF -> Res.string.accountant_format_attendance_pdf to Res.string.accountant_format_attendance_pdf_text
    AccountantExportFormat.GENERIC_CSV -> Res.string.accountant_format_generic_csv to Res.string.accountant_format_generic_csv_text
    AccountantExportFormat.OPTIMA_RCP -> Res.string.accountant_format_optima to Res.string.accountant_format_optima_text
    AccountantExportFormat.SYMFONIA_RCP -> Res.string.accountant_format_symfonia to Res.string.accountant_format_symfonia_text
    AccountantExportFormat.ENOVA_RCP -> Res.string.accountant_format_enova to Res.string.accountant_format_enova_text
    AccountantExportFormat.PACKAGE -> Res.string.accountant_format_package to Res.string.accountant_format_package_text
}

/**
 * "For the accountant" (SH-117, Labour cost): pick the working-time record, the attendance list
 * or an import file for Optima, Symfonia or enova365 (docs/exports.md), then download it or send
 * it to the accountant's address. The link is opened on the person's tap (a link opened after a
 * wait would be blocked as a pop-up on the web). Names the people an import file left out for
 * lack of a payroll ID.
 */
@Composable
fun AccountantExportDialog(rosterId: String, onDismiss: () -> Unit) {
    val viewModel: AccountantExportViewModel = diViewModel(rosterId)
    val state by viewModel.state.collectAsState()
    val uri = LocalUriHandler.current
    LaunchedEffect(Unit) { viewModel.reset() }
    val ready = state.ready

    EvoDialog(
        title = stringResource(Res.string.accountant_export_title),
        icon = Icons.Default.Description,
        onDismiss = onDismiss,
        dismissible = !state.working,
        confirm = if (ready != null) {
            DialogAction(stringResource(Res.string.accountant_open_file), { uri.openUri(ready.url) }, icon = Icons.Default.Download)
        } else {
            DialogAction(
                stringResource(Res.string.accountant_download),
                viewModel::download,
                enabled = !state.working,
                loading = state.working && state.emailTo.isBlank(),
                icon = Icons.Default.Download
            )
        },
        neutral = if (ready != null) null else DialogAction(
            stringResource(Res.string.accountant_send),
            viewModel::send,
            enabled = state.canSend,
            loading = state.working && state.emailTo.isNotBlank(),
            icon = Icons.AutoMirrored.Filled.Send
        ),
        dismiss = DialogAction(stringResource(Res.string.close), onDismiss, enabled = !state.working),
        bodyArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (ready != null) {
            Text(stringResource(Res.string.accountant_ready, ready.fileName), color = Color.White, style = MaterialTheme.typography.bodyLarge)
            ready.emailedTo?.let { to ->
                Text(stringResource(Res.string.accountant_sent, to), color = Color.White, style = MaterialTheme.typography.bodyMedium)
                if (!ready.attached) {
                    Text(stringResource(Res.string.accountant_sent_link_only), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
                }
            }
            if (ready.withoutPayrollId.isNotEmpty()) {
                Text(
                    stringResource(Res.string.accountant_missing_payroll_ids, ready.withoutPayrollId.joinToString(", ")),
                    color = ErrorOnNavy,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        } else {
            Text(
                stringResource(Res.string.accountant_export_intro),
                color = Color.White.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(bottom = 4.dp)
            )
            EvoRadioGroup(
                options = AccountantExportFormat.entries,
                selected = state.format,
                onSelect = viewModel::choose,
                optionLabel = { stringResource(it.labels().first) },
                description = { stringResource(it.labels().second) }
            )
            EvoTextField(
                value = state.emailTo,
                onValueChange = viewModel::updateEmail,
                label = stringResource(Res.string.accountant_email_label),
                keyboardType = KeyboardType.Email,
                capitalization = KeyboardCapitalization.None,
                enabled = !state.working
            )
        }
        state.error?.let {
            Text(it.asString(), color = ErrorOnNavy, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(top = 8.dp))
        }
    }
}
